import React, { ReactNode, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Card, Button } from "antd";
import { LogoutOutlined } from "@ant-design/icons";

import { Footer } from "@components/Page";
import { getConfig } from "@components/Config";
import { useAppContext } from "@components/AppContext";
import { type AppContext } from "@components/AppContext/types";
import MyProfile from "@components/MyProfile";
import { OrganizationsList } from "@components/Organization";
import { GoToInvestmentForm } from "@components/Investment";

import ReportsList from "../reports/ReportsList.js";

const PLATFORM_NAME = getConfig("PLATFORM_NAME");

const LABEL_TITLE = "Console";
const LABEL_SIGNOUT = "Sign Out";
const LABEL_REPORTS = "Reports";
const LABEL_ORGANIZATIONS = "Organizations";
const LABEL_MY_PROFILE = "My Profile";
const LABEL_GO_TO_INVESTMENT = "Investments";

const LOGOUT_PATH = "/logout/backstage";

const TAB_KEYS = {
  organizations: "organizations",
  reports: "reports",
  myProfile: "myProfile",
  goToInvestment: "goToInvestment",
} as const;

type TabKey = (typeof TAB_KEYS)[keyof typeof TAB_KEYS]

const TAB_PATHS: Record<TabKey, string> = {
  [TAB_KEYS.organizations]: "/backstage/organizations",
  [TAB_KEYS.reports]: "/backstage/reports",
  [TAB_KEYS.myProfile]: "/backstage/my-profile",
  [TAB_KEYS.goToInvestment]: "/backstage/go-to-investment",
};

const PATH_TO_TAB_KEY: Record<string, TabKey> = {
  "/backstage": TAB_KEYS.organizations,
  "/backstage/organizations": TAB_KEYS.organizations,
  "/backstage/reports": TAB_KEYS.reports,
  "/backstage/my-profile": TAB_KEYS.myProfile,
  "/backstage/go-to-investment": TAB_KEYS.goToInvestment,
};

const tabList = [
  {
    key: TAB_KEYS.organizations,
    tab: LABEL_ORGANIZATIONS,
  },
  {
    key: TAB_KEYS.goToInvestment,
    tab: LABEL_GO_TO_INVESTMENT,
  },
  {
    key: TAB_KEYS.reports,
    tab: LABEL_REPORTS,
  },
  {
    key: TAB_KEYS.myProfile,
    tab: LABEL_MY_PROFILE,
  },
];

const ConsoleManagerPage = () => {
  const {
    identity,
    updateIdentityMobile,
    updateIdentityIs2FaEnabled,
  } = useAppContext() as AppContext;

  const [activeTabKey, setActiveTabKey] = useState<TabKey>(TAB_KEYS.organizations);

  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const pathTabKey = PATH_TO_TAB_KEY[location.pathname] || TAB_KEYS.organizations;
    const shouldSyncTabWithPath = pathTabKey !== activeTabKey;

    if (shouldSyncTabWithPath) {
      setActiveTabKey(pathTabKey);
    }
  }, [location.pathname, activeTabKey]);

  const onTabChange = (key: string) => {
    const isValidTabKey = Object.values(TAB_KEYS).includes(key as TabKey);
    if (isValidTabKey) {
      const nextTabKey = key as TabKey;
      setActiveTabKey(nextTabKey);
      navigate(TAB_PATHS[nextTabKey], { replace: true });
    }
  };

  const extra = (
    <Button
      type="text"
      size="small"
      icon={<LogoutOutlined />}
      onClick={() => navigate(LOGOUT_PATH)}
    >
      {LABEL_SIGNOUT}
    </Button>
  );

  const cardProps = {
    title: LABEL_TITLE,
    tabProps: { size: "middle" as const },
    extra,
    tabList,
    activeTabKey,
    onTabChange,
  };

  const givenName = identity?.givenName;
  const familyName = identity?.familyName;
  const fullName = `${givenName || ""} ${familyName || ""}`.trim();
  const onSuccessUpdateMobile = (updatedMobile: string) => {
    updateIdentityMobile?.(updatedMobile);
  };
  const onSuccessSwitch2Fa = (shouldEnable: boolean) => {
    updateIdentityIs2FaEnabled?.(shouldEnable);
  };

  const tabContent: Record<TabKey, ReactNode> = {
    [TAB_KEYS.organizations]: <OrganizationsList />,
    [TAB_KEYS.reports]: <ReportsList span={24} />,
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    [TAB_KEYS.goToInvestment]: <GoToInvestmentForm onSuccess={() => {}} />,
    [TAB_KEYS.myProfile]: (
      <MyProfile
        userId={identity?.id || ""}
        name={fullName}
        email={identity?.email}
        mobile={identity?.mobile}
        is2FaEnabled={identity?.is2FaEnabled}
        onSuccessUpdateMobile={onSuccessUpdateMobile}
        onSuccessSwitch2Fa={onSuccessSwitch2Fa}
      />
    ),
  };

  return (
    <>
      <Card {...cardProps}>{tabContent[activeTabKey]}</Card>

      <Footer organizationName={PLATFORM_NAME} />
    </>
  );
};

export default ConsoleManagerPage;
